import { useCallback, useSyncExternalStore } from 'react';

/**
 * THE FRAME'S ONE BREAKPOINT, AND WHY IT IS A MEDIA QUERY IN JAVASCRIPT
 * RATHER THAN A TAILWIND VARIANT.
 *
 * `md:hidden` would hide the menu column with CSS and leave the reveal button
 * in the document at every width — present, focusable by a screen reader's
 * own traversal in some engines, and above all INVISIBLE TO ANY ASSERTION,
 * because the environment these screens are tested in performs no layout and
 * applies no stylesheet. A frame whose two shapes cannot be told apart by a
 * test is a frame with one tested shape and one hoped-for one.
 *
 * Reading the query in JavaScript makes the two shapes two different DOMs: at
 * the wide reading the persistent column is mounted and there is no reveal
 * button anywhere; at the narrow reading the button exists and the column does
 * not. Both are then ordinary assertions about elements.
 *
 * ── WHY 768 AND NOT SOME OTHER NUMBER ─────────────────────────────────────
 *
 * The frame consumes a fixed 320px before any screen gets a pixel: the menu
 * column is 256px (`sidebar.tsx`, §4.3.1) and the page column adds 64px of
 * gutter (`app-shell.tsx`, §4.3.4). That is a property of the frame, not a
 * choice made here.
 *
 * 768 is Tailwind's `md`, which is ALREADY the only boundary the shell has:
 * the top bar's search box and its trailing control row both switch there
 * (`topbar.tsx`). Reusing it means the frame changes shape at ONE width
 * instead of two — a reader never meets a window in which the top bar has
 * rearranged itself and the menu column has not, or the reverse.
 *
 * Expressed as the WIDE condition rather than the narrow one so it reads the
 * same way Tailwind's own `md:` does (min-width, mobile-first), leaving one
 * negation in one place instead of an off-by-a-hundredth `max-width`.
 */
export const WIDE_FRAME_QUERY = '(min-width: 768px)';

/**
 * Subscribes a component to a CSS media query.
 *
 * `useSyncExternalStore` and not `useState` + an effect: the media list IS an
 * external store, and reading it during render is what keeps the first paint
 * from being the wrong shape followed by a correction. A user opening the
 * product on a narrow window must not see the desktop frame flash first.
 *
 * The snapshot is a boolean, so React's identity comparison settles
 * immediately; returning the `MediaQueryList` object itself would re-render
 * forever, since `matchMedia` answers with a new object every call.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    // The server snapshot. There is no server renderer in this product, and
    // this exists so the hook is total rather than as a claim that one is
    // coming: the wide frame is what the specification describes, so a
    // renderer that cannot measure a window answers with the shape §4.3 draws.
    () => true,
  );
}

/**
 * True when the window is wide enough for §4.3's persistent menu column.
 *
 * The one caller that matters is the shell. Every other consumer of the
 * frame's shape should ask the shell rather than call this a second time, so
 * that "narrow" is one reading per render and cannot disagree with itself
 * between the button and the panel it opens.
 */
export function useWideFrame(): boolean {
  return useMediaQuery(WIDE_FRAME_QUERY);
}
